// ============================================================================
// Processing drawer location projection
// ============================================================================
//
// Processing owns drawer state locally, but mirrors its current record into
// the URL with a REPLACE, never a PUSH.  Keeping the projection pure lets the
// facade own the actual history write and pins the existing no-history-spam
// contract ahead of the router cutover.
// ============================================================================

import type {AppLocation} from '../app/navigation/appLocation.js';
import type {AppNavigate} from '../app/navigation/appNavigate.js';

export function readProcessingDeepLink(
  search: string,
): {recordId: string} | {sourceKind: string; sourceId: string} | null {
  const params = new URLSearchParams(search);
  const record = params.get('record');
  if (record) return {recordId: record};
  const source = params.get('source') || '';
  const sep = source.indexOf(':');
  if (sep > 0 && sep < source.length - 1) {
    return {sourceKind: source.slice(0, sep), sourceId: source.slice(sep + 1)};
  }
  return null;
}

/** Return the facade destination that preserves all non-Processing URL data. */
export function processingDrawerDestination(
  location: Pick<AppLocation, 'pathname' | 'search' | 'hash'>,
  openRecordId: string | null,
): string {
  const params = new URLSearchParams(location.search);
  if (openRecordId) params.set('record', openRecordId);
  else params.delete('record');
  // source is an inbound, one-shot selector. The current drawer state has won
  // once deeplinkReady is true, so leaving it would make a refresh ambiguous.
  params.delete('source');
  const query = params.toString();
  return location.pathname + (query ? `?${query}` : '') + location.hash;
}

/**
 * Replace the current entry only when the drawer projection actually differs.
 * Returning a boolean makes the no-op branch observable without a browser.
 */
export function replaceProcessingDrawerLocation(
  location: AppLocation,
  openRecordId: string | null,
  navigate: AppNavigate,
): boolean {
  const next = processingDrawerDestination(location, openRecordId);
  const current = location.pathname + location.search + location.hash;
  if (next === current) return false;
  navigate(next, {replace: true, state: location.state});
  return true;
}
